import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import API_ENDPOINTS from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { useSpeech } from './useSpeech';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

export interface ChatConversation {
  id: string;
  title: string;
  is_active: boolean;
  status: string;
}

export interface UseSimpleChatArgs {
  onConversationStateChange?: (isClosed: boolean) => void;
  conversationId?: string;
}

export interface UseSimpleChatResult {
  conversation: ChatConversation | null;
  messages: ChatMessage[];
  inputMessage: string;
  loading: boolean;
  initializing: boolean;
  error: string | null;
  setError: (next: string | null) => void;
  isConversationClosed: boolean;
  ttsEnabled: boolean;
  setTtsEnabled: (next: boolean) => void;
  setInputMessage: (next: string) => void;
  handleKeyPress: (e: React.KeyboardEvent) => void;
  sendMessageContent: (content: string) => Promise<void>;
  handleSendMessage: () => Promise<void>;
  handleCloseConversation: () => Promise<void>;
  handleStartNewChat: () => Promise<void>;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  speech: ReturnType<typeof useSpeech>;
}

export function useSimpleChat(args: UseSimpleChatArgs): UseSimpleChatResult {
  const { token, user } = useAuth();

  const [conversation, setConversation] = useState<ChatConversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConversationClosed, setIsConversationClosed] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const [initializationStarted, setInitializationStarted] = useState(false);

  const speech = useSpeech();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const scrollToBottom = useCallback((): void => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();

    if (ttsEnabled && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.role === 'assistant') {
        const voiceGender = (user?.voice_gender as 'male' | 'female') || 'male';
        const voiceLanguage = user?.voice_language || 'en-GB';
        speech.speak(lastMessage.content, undefined, voiceGender, voiceLanguage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, ttsEnabled, user]);

  useEffect(() => {
    if (!conversation) {
      return;
    }

    const isClosed =
      !conversation.is_active ||
      conversation.status === 'completed' ||
      conversation.status === 'closed';

    setIsConversationClosed(isClosed);
    args.onConversationStateChange?.(isClosed);
  }, [conversation, args.onConversationStateChange]);

  const loadMessages = useCallback(async (conversationId: string): Promise<void> => {
    try {
      const response = await axios.get(
        API_ENDPOINTS.CONVERSATIONS.MESSAGES(conversationId),
      );
      setMessages(response.data.results || []);
    } catch (loadError: any) {
      // Reason: a failed reload isn't fatal for the UI
      console.error('Failed to load messages:', loadError);
    }
  }, []);

  // Poll the backend for conversation status changes (e.g. server-side auto-closure)
  const stopPolling = useCallback((): void => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const startPolling = useCallback((conversationId: string): void => {
    stopPolling();

    pollingRef.current = setInterval(async () => {
      try {
        const response = await axios.get(
          API_ENDPOINTS.CONVERSATIONS.DETAIL(conversationId),
        );
        const data = response.data;
        const isClosed =
          !data.is_active ||
          data.status === 'completed' ||
          data.status === 'closed';

        if (isClosed) {
          setConversation(data);
          setIsConversationClosed(true);
          // Reload messages to pick up the closing message from the backend
          await loadMessages(conversationId);
          stopPolling();
        }
      } catch (pollError: unknown) {
        // Reason: polling errors are non-fatal; log and continue
        console.error('Status poll failed:', pollError);
      }
    }, 30000);
  }, [stopPolling, loadMessages]);

  const sendOpeningMessage = useCallback(
    async (conversationId: string): Promise<void> => {
      try {
        const response = await axios.post(
          API_ENDPOINTS.CONVERSATIONS.SEND_MESSAGE(conversationId),
          {
            content: 'SYSTEM_OPENING_MESSAGE',
          },
        );

        if (response.data.ai_response) {
          setMessages([response.data.ai_response]);
          startPolling(conversationId);
        }
      } catch (openingError: any) {
        console.error('Failed to send opening message:', openingError);
        setError('Failed to initialize conversation. Please try again.');
      }
    },
    [startPolling],
  );

  const initializeConversation = useCallback(async (): Promise<void> => {
    if (initializationStarted) {
      return;
    }

    try {
      setInitializationStarted(true);
      setInitializing(true);
      setError(null);

      // If a specific conversationId was provided, load it directly
      if (args.conversationId) {
        const response = await axios.get(
          API_ENDPOINTS.CONVERSATIONS.DETAIL(args.conversationId),
        );
        const conversationData = response.data;
        setConversation(conversationData);
        await loadMessages(conversationData.id);
        return;
      }

      // Reason: filter to general conversations only so habit discussions
      // (conversation_type=goal_setting) are not picked up as resumable chats
      const existingResponse = await axios.get(
        `${API_ENDPOINTS.CONVERSATIONS.LIST}?is_active=true&conversation_type=general&ordering=-created_at&limit=1`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (existingResponse.data.results && existingResponse.data.results.length > 0) {
        const conversationData = existingResponse.data.results[0];

        if (!conversationData?.id) {
          throw new Error('Invalid conversation: missing ID');
        }

        setConversation(conversationData);
        await loadMessages(conversationData.id);
        return;
      }

      const response = await axios.post(
        API_ENDPOINTS.CONVERSATIONS.LIST,
        {
          conversation_type: 'general',
          title: 'Chat Session',
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      const conversationData = response.data;

      if (!conversationData?.id) {
        throw new Error('Invalid conversation: missing ID');
      }

      setConversation(conversationData);
      await loadMessages(conversationData.id);
      await sendOpeningMessage(conversationData.id);
    } catch (initError: any) {
      console.error('Failed to initialize conversation:', initError);
      console.error('Error details:', initError.response?.data);
      console.error('Error status:', initError.response?.status);
      setError('Failed to start chat session');
      setInitializationStarted(false);
    } finally {
      setInitializing(false);
    }
  }, [initializationStarted, token, args.conversationId, loadMessages, sendOpeningMessage]);

  useEffect(() => {
    if (!initializationStarted) {
      initializeConversation();
    }
  }, [initializationStarted, initializeConversation]);

  const pollForAiResponse = useCallback(
    async (conversationId: string, cursor: string): Promise<void> => {
      // Reason: short-poll the events endpoint for the AI response.
      // Web uses wait_ms=3000 (short hold) for simplicity; mobile will
      // use longer holds via TanStack Query in a later phase.
      const maxAttempts = 30;
      let currentCursor = cursor;

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          const eventsResponse = await axios.get(
            API_ENDPOINTS.CONVERSATIONS.EVENTS(conversationId),
            { params: { after: currentCursor, wait_ms: 3000 } },
          );

          const newMessages: ChatMessage[] = eventsResponse.data.messages || [];
          if (newMessages.length > 0) {
            currentCursor = eventsResponse.data.cursor;
            setMessages((prev) => {
              const existingIds = new Set(prev.map((m) => m.id));
              const unique = newMessages.filter((m) => !existingIds.has(m.id));
              return [...prev, ...unique];
            });

            // Reason: check if we got an assistant message — if so, done
            const hasAssistant = newMessages.some((m) => m.role === 'assistant');
            if (hasAssistant) {
              return;
            }
          }
        } catch (pollError: unknown) {
          console.error('Events poll failed:', pollError);
        }

        // Reason: small delay between retries to avoid hammering
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    },
    [],
  );

  const sendMessageContent = useCallback(
    async (content: string): Promise<void> => {
      if (!content.trim() || !conversation || loading) {
        return;
      }

      setLoading(true);

      try {
        const clientMsgId = `web-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

        const tempUserMessage: ChatMessage = {
          id: `temp-${Date.now()}`,
          role: 'user',
          content: content.trim(),
          created_at: new Date().toISOString(),
        };

        setMessages((prev) => [...prev, tempUserMessage]);

        const response = await axios.post(
          API_ENDPOINTS.CONVERSATIONS.SEND_MESSAGE(conversation.id),
          {
            content: content.trim(),
            role: 'user',
            client_msg_id: clientMsgId,
          },
        );

        // Replace temp message with server-confirmed user message
        if (response.data.user_message) {
          setMessages((prev) => {
            const withoutTemp = prev.filter((m) => !m.id.startsWith('temp-'));
            return [...withoutTemp, response.data.user_message];
          });
        }

        // Handle async response: poll for AI message via events endpoint
        if (response.data.status === 'processing' && response.data.cursor) {
          await pollForAiResponse(conversation.id, response.data.cursor);
        }
      } catch (sendError: any) {
        console.error('Failed to send message:', sendError);
        // Reason: surface explicit backend messages where present so the
        // user knows whether they hit the hourly throttle (429), the
        // length cap (400), or a generic failure. Falls back to a
        // friendly default for anything else.
        const status = sendError?.response?.status;
        const data = sendError?.response?.data;
        if (status === 429) {
          const retryAfter = sendError?.response?.headers?.['retry-after'];
          const mins = retryAfter
            ? Math.max(1, Math.ceil(Number(retryAfter) / 60))
            : null;
          setError(
            mins
              ? `You've sent a lot of messages recently. Please try again in about ${mins} minute${mins === 1 ? '' : 's'}.`
              : "You've sent a lot of messages recently. Please try again shortly.",
          );
        } else if (status === 400) {
          // Reason: DRF returns serializer field errors under the field name
          // (e.g. {"content": ["Message is too long. Maximum 1000 characters."]})
          // and top-level errors under "detail". Surface whichever is present.
          const fieldErr = Array.isArray(data?.content) ? data.content[0] : null;
          const nonFieldErr = Array.isArray(data?.non_field_errors)
            ? data.non_field_errors[0]
            : null;
          setError(
            data?.detail || fieldErr || nonFieldErr || 'Message was rejected.',
          );
        } else {
          setError('Failed to send message');
        }
        setMessages((prev) => prev.filter((m) => !m.id.startsWith('temp-')));
      } finally {
        setLoading(false);
      }
    },
    [conversation, loading, pollForAiResponse],
  );

  const handleSendMessage = useCallback(async (): Promise<void> => {
    const content = inputMessage;
    setInputMessage('');
    await sendMessageContent(content);
  }, [inputMessage, sendMessageContent]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent): void => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void handleSendMessage();
      }
    },
    [handleSendMessage],
  );

  const handleCloseConversation = useCallback(async (): Promise<void> => {
    if (!conversation || !token) {
      return;
    }

    stopPolling();

    try {
      setLoading(true);
      setError(null);

      const response = await axios.post(
        API_ENDPOINTS.CONVERSATIONS.CLOSE(conversation.id),
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (response.data.closing_message) {
        setMessages((prev) => [...prev, response.data.closing_message]);
      }

      if (response.data.conversation) {
        setConversation(response.data.conversation);
        setIsConversationClosed(true);
      }

      setInputMessage('');
    } catch (closeError: any) {
      console.error('Failed to close conversation:', closeError);
      setError('Failed to close conversation');
    } finally {
      setLoading(false);
    }
  }, [conversation, token, stopPolling]);

  const handleStartNewChat = useCallback(async (): Promise<void> => {
    if (!token) {
      return;
    }

    stopPolling();

    try {
      setLoading(true);
      setError(null);

      // Close current conversation if it's still active
      if (conversation && !isConversationClosed) {
        await axios.post(
          API_ENDPOINTS.CONVERSATIONS.CLOSE(conversation.id),
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        );
      }

      // Reset state for new conversation
      setConversation(null);
      setMessages([]);
      setIsConversationClosed(false);
      setInputMessage('');
      setInitializationStarted(false);
    } catch (newChatError: any) {
      console.error('Failed to start new chat:', newChatError);
      setError('Failed to start new chat');
    } finally {
      setLoading(false);
    }
  }, [conversation, token, isConversationClosed, stopPolling]);

  // Start polling when we have an active conversation
  useEffect(() => {
    if (conversation?.id && !isConversationClosed) {
      startPolling(conversation.id);
    }
    return () => {
      stopPolling();
    };
  }, [conversation?.id, isConversationClosed, startPolling, stopPolling]);

  // Stop polling when conversation closes
  useEffect(() => {
    if (isConversationClosed) {
      stopPolling();
    }
  }, [isConversationClosed, stopPolling]);

  return {
    conversation,
    messages,
    inputMessage,
    loading,
    initializing,
    error,
    setError,
    isConversationClosed,
    ttsEnabled,
    setTtsEnabled,
    setInputMessage,
    handleKeyPress,
    sendMessageContent,
    handleSendMessage,
    handleCloseConversation,
    handleStartNewChat,
    messagesEndRef,
    speech,
  };
}
